import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import clsx from 'clsx';
import { NotificationContext } from './context';
import { useSignalRNotifications } from 'hooks/useSignalRNotifications';
import notificationSound from 'assets/audio/notification.wav';
import router from 'app/router/router';
import { renderNotification } from 'utils/notificationTemplates';

import { BellIcon } from '@heroicons/react/24/outline';
import { FaWhatsapp, FaInstagram } from 'react-icons/fa';
import { SiGoogleads } from 'react-icons/si';

const notificationIcons = {
  whatsapp: { icon: FaWhatsapp, bg: 'bg-green-100 text-green-600' },
  instagram: { icon: FaInstagram, bg: 'bg-pink-100 text-pink-600' },
  google: { icon: SiGoogleads, bg: 'bg-blue-100 text-blue-600' },
  system: { icon: BellIcon, bg: 'bg-gray-100 text-gray-600' },
  default: { icon: BellIcon, bg: 'bg-gray-100 text-gray-600' },
};

const colorStyles = {
  info: 'bg-blue-100 text-blue-600',
  success: 'bg-green-100 text-green-600',
  warning: 'bg-yellow-100 text-yellow-600',
  error: 'bg-red-100 text-red-600',
  neutral: 'bg-gray-100 text-gray-600',
  simple: 'bg-purple-100 text-purple-600',
  pink: 'bg-pink-100 text-pink-600',
};

/**
 * Notification Provider
 * Manages SignalR real-time notifications for the entire application
 */
export function NotificationProvider({ children }) {
  // Get access token from localStorage
  const accessToken = typeof window !== 'undefined'
    ? localStorage.getItem('authToken')
    : null;

  // Create audio reference for notification sound
  const audioRef = useRef(null);

  // Initialize audio on mount
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const audio = new Audio(notificationSound);
    audio.preload = 'auto';
    audio.volume = 0.5; // Set volume to 50%
    audio.load();
    audioRef.current = audio;

    const unlockAudio = () => {
      if (!audioRef.current) {
        return;
      }

      audioRef.current
        .play()
        .then(() => {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        })
        .catch(() => {
          // Ignore unlock errors (browser may block until user interacts again)
        });

      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };

    window.addEventListener('pointerdown', unlockAudio, { once: true });
    window.addEventListener('keydown', unlockAudio, { once: true });

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Play notification sound
  const playNotificationSound = () => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(notificationSound);
        audioRef.current.preload = 'auto';
        audioRef.current.volume = 0.5;
      }
      audioRef.current.currentTime = 0;
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn('Failed to play notification sound:', error);
        });
      }
    } catch (e) {
      console.warn('Audio playback error:', e);
    }
  };

  // Track recently processed notification IDs to prevent duplicate toast popups
  const recentNotificationsRef = useRef(new Set());

  // Initialize SignalR notifications
  const notificationState = useSignalRNotifications({
    accessToken,
    autoConnect: !!accessToken, // Only auto-connect if token exists
    onNotification: (notification) => {
      // Do not show notifications on public pages
      if (typeof window !== 'undefined') {
        const path = window.location.pathname.toLowerCase();
        if (path.startsWith('/form/') || path.startsWith('/form-thankyou/') || path.startsWith('/qr/') || path.startsWith('/whs/')) {
          return;
        }
      }

      // Deduplicate recent notifications within a 3-second window
      const notifId = notification.userNotificationId || notification.UserNotificationId || notification.id;
      if (notifId && recentNotificationsRef.current.has(String(notifId))) {
        return;
      }
      if (notifId) {
        recentNotificationsRef.current.add(String(notifId));
        setTimeout(() => {
          recentNotificationsRef.current.delete(String(notifId));
        }, 3000);
      }

      // Play notification sound
      playNotificationSound();

      // Ensure template details are resolved (title, bodyParts, icon, color, action)
      let notif = notification;
      if (!notif.title || !notif.bodyParts) {
        const eventTypeCode = String(
          notification.eventTypeCode ||
          notification.EventTypeCode ||
          notification.userNotificationType ||
          notification.UserNotificationType ||
          ''
        );
        const payload = notification.payload || notification.Payload || {};
        if (eventTypeCode) {
          const renderedDetails = renderNotification(eventTypeCode, payload);
          notif = {
            ...notification,
            ...renderedDetails,
          };
        }
      }

      // Display real-time toast matching the notification panel card design in top-right
      toast.custom(
        (t) => {
          const typeKey = (notif.source || '').toLowerCase();
          const TemplateIcon = notif.icon;
          const config = notificationIcons[typeKey] || notificationIcons.default;
          const Icon = TemplateIcon || config.icon || BellIcon;
          const targetLink = notif.action || notif.link;

          return (
            <div
              onClick={() => {
                toast.dismiss(t);
                if (targetLink && targetLink !== '#') {
                  try {
                    router.navigate(targetLink);
                  } catch (err) {
                    window.location.href = targetLink;
                  }
                }
              }}
              className={clsx(
                "group flex items-start gap-3 rounded-xl p-3 shadow-xl border cursor-pointer transition-all duration-200 w-80 sm:w-96 select-none",
                "bg-white border-gray-200 hover:bg-gray-50 dark:bg-dark-700 dark:border-dark-600 dark:hover:bg-dark-600"
              )}
            >
              {/* UNREAD DOT */}
              <span className="mt-2 w-2 h-2 rounded-full bg-primary-600 shrink-0" />

              {/* ICON CONTAINER */}
              <div
                className={clsx(
                  "flex items-center justify-center h-10 w-10 rounded-lg p-1 shrink-0",
                  colorStyles[notif.color] || config.bg
                )}
              >
                <Icon className="text-lg" />
              </div>

              {/* CONTENT */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm text-gray-900 dark:text-dark-100 truncate">
                    {notif.title || 'New Notification'}
                  </p>
                  <span className="text-xs text-gray-400 whitespace-nowrap ml-2 shrink-0">
                    Just now
                  </span>
                </div>

                <div className="text-xs text-gray-600 dark:text-dark-300 mt-1 line-clamp-3 leading-relaxed">
                  {Array.isArray(notif.bodyParts) && notif.bodyParts.length > 0 ? (
                    notif.bodyParts.map((part, idx) => {
                      if (part.type === 'break') return <br key={idx} />;
                      return <span key={idx}>{part.text}</span>;
                    })
                  ) : (
                    <span>{notif.body || notif.message || notif.Message || ''}</span>
                  )}
                </div>
              </div>
            </div>
          );
        },
        {
          position: 'top-right',
          duration: 8000,
        }
      );
    },
    onError: (error) => {
      console.error('❌ Notification error:', error);

      // Only show error toast in development or for critical errors
      if (import.meta.env.DEV) {
        toast.error('Notification service error', {
          description: error.message || 'Failed to connect to notification service',
        });
      }
    }
  });


  return (
    <NotificationContext.Provider value={notificationState}>
      {children}
    </NotificationContext.Provider>
  );
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

